import { Injectable } from '@angular/core';
import { CONFIG } from '../../config';
import { DateTime } from 'luxon';


/**
 * Injectable service to handle date and time operations
 */
@Injectable()
export class DateTimeService {

  constructor() { }

  /**
   * Converts the UI date/time to the server required format
   * @param {string} dateValue
   * @param {string} dateFormat
   * @param {string} timezone
   * @returns {string}
   */
  public static convertToServerDateTime (dateValue: string, dateFormat: string, timezone: string): string {
    return DateTime
      .fromFormat(dateValue, dateFormat, { zone: timezone })
      .toUTC()
      .toFormat(CONFIG.dateTimeServerFormat);
  }

  /**
   * Converts the date from se server (UTC) to UI format (EST)
   * @param {string} dateValue
   * @param {string} timezone
   * @param {string} dateFormat
   * @returns {string}
   */
  public static convertToEst (dateValue: string, timezone: string = CONFIG.formatEST, dateFormat: string = CONFIG.dateTimeServerFormat): string {
    if (dateValue) {
      const date = this.parseDate(dateValue);
      if (date.isValid) {
        return date.setZone(timezone).toFormat(dateFormat);
      }
    }
    return '';
  }

  /**
   *  Returns the current timezone (e.g. 'America/New_York')
   */
  public static getCurrentTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /**
   * Returns the current quarter of the year.
   */
  public static getCurrentQuarter (): string {
    const currentTime = DateTime.now().toLocal();
    return `${currentTime.quarter}Q${currentTime.year.toString().slice(-2)}`;
  }

  /**
   * Attempt to parse the given string into a DateTime from our most commonly used formats or a given format
   * @param value The date to parse
   * @param timezone (Optional) The timezone to parse the date in
   * @param formats (Optional) One or more formats to parse the date from. Attempts to parse each in order
   */
  public static parseDate(value: string | Date, timezone?: string, formats?: string | Array<string>): DateTime {
    if (!value) {
      return null;
    }
    if (typeof value === 'string') {
      let parsed = DateTime.fromFormat(value, CONFIG.dateTimeServerFormat, { zone: timezone });
      if (!parsed.isValid) {
        parsed = DateTime.fromISO(value, { zone: timezone });
      }
      if (!parsed.isValid) {
        parsed = DateTimeService.parseFromFormat(value, timezone, formats) || parsed;
      }

      return parsed;
    } else {
      return DateTime.fromJSDate(value, { zone: timezone });
    }
  }

  private static parseFromFormat(value: string, timezone?: string, formats?: string | Array<string>) {
    formats = Array.isArray(formats) ? formats : [formats];
    formats = [CONFIG.shortDateFormat, ...formats].filter(x => !!x);
    for (const format of formats) {
      const dt = DateTime.fromFormat(value, format, { zone: timezone });
      if (dt.isValid) {
        return dt;
      }
    }
    return null;
  }

}
